import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Inbox, Loader2, MoreHorizontal, X } from "lucide-react";

import { useNow } from "../lib/useNow.js";

/** The small shared pieces. Kept in one file because none of them earns its own. */

export function Spinner({ size = 16, className = "" }) {
    return <Loader2 size={size} className={`animate-spin ${className}`} aria-label="loading" />;
}

export function EmptyState({ icon: Icon = Inbox, title, hint, action }) {
    return (
        <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
            <Icon size={28} className="muted" aria-hidden />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</p>
            {hint ? <p className="muted max-w-md text-sm">{hint}</p> : null}
            {action}
        </div>
    );
}

export function Modal({ open, onClose, title, children, width = "max-w-lg" }) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => event.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-8">
            <div className={`card w-full ${width} shadow-xl`} role="dialog" aria-modal="true" aria-label={title}>
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-800">
                    <h2 className="text-sm font-semibold">{title}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="close">
                        <X size={16} />
                    </button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}

export function Tabs({ tabs, active, onChange }) {
    return (
        /*
         * Scrolls itself rather than taking the page with it: a project has six
         * tabs and 375px fits about four.
         *
         * Two details keep it to *one* axis. Setting only `overflow-x` makes the
         * other compute to `auto` rather than staying `visible`, so `overflow-y`
         * is pinned; and the `-mb-px` that laps the active tab's underline over
         * the grey rule leaves the content a pixel taller than the box, so the
         * box is given that pixel back. Without the padding the strip scrolled
         * vertically by one pixel — a tab bar that wobbles under your thumb — and
         * pinning the axis alone would instead have clipped half the underline.
         */
        <div className="no-scrollbar -mx-4 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-gray-200 px-4 pb-px sm:mx-0 sm:px-0 dark:border-gray-800" role="tablist">
            {tabs.map((tab) => (
                <button
                    key={tab.id}
                    role="tab"
                    aria-selected={active === tab.id}
                    onClick={() => onChange(tab.id)}
                    className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                        active === tab.id
                            ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                            : "border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    }`}
                >
                    {tab.label}
                    {tab.count !== undefined ? <span className="muted ml-1.5 text-xs">{tab.count}</span> : null}
                </button>
            ))}
        </div>
    );
}

/**
 * A button and a panel under it: an overflow menu, or anything else.
 *
 * Pass `items` for the ordinary case and it renders them; pass `children` when
 * the panel is not a list — the session control wants a couple of lines of state
 * above its action.
 *
 * The panel is clamped to the viewport. Anchoring it to the right edge of a
 * trigger that sits near the left of a 375px screen put a 15rem panel half
 * off-screen, with the labels cut down the middle and no way to scroll to them.
 *
 * An item that is `busy` spins, in the list and on the trigger — the menu is
 * closed by the time the work happens, so the trigger is what the row shows.
 */
export function Menu({ items, children, trigger, label = "Actions", width = "w-60" }) {
    const [open, setOpen] = useState(false);
    const [shift, setShift] = useState(0);
    const box = useRef(null);
    const panel = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onAway = (event) => !box.current?.contains(event.target) && setOpen(false);
        const onKey = (event) => event.key === "Escape" && setOpen(false);
        document.addEventListener("pointerdown", onAway);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("pointerdown", onAway);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // Measured after it is on screen, because where it lands depends on where the
    // trigger is, and that moves with the layout.
    useEffect(() => {
        if (!open || !panel.current) return;
        const rect = panel.current.getBoundingClientRect();
        const margin = 8;
        if (rect.left < margin) setShift(margin - rect.left);
        else if (rect.right > window.innerWidth - margin) setShift(window.innerWidth - margin - rect.right);
        else setShift(0);
    }, [open]);

    if (!items && !children) return null;
    if (items && items.length === 0) return null;

    const working = items?.some((action) => action.busy) ?? false;

    return (
        <div className="relative shrink-0" ref={box}>
            <button
                onClick={() => setOpen((value) => !value)}
                aria-label={label}
                aria-expanded={open}
                aria-busy={working || undefined}
                className={trigger ? "" : "muted flex size-9 items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"}
            >
                {trigger ?? (working ? <Spinner size={16} /> : <MoreHorizontal size={16} />)}
            </button>

            {open ? (
                <div
                    ref={panel}
                    style={{ transform: shift ? `translateX(${shift}px)` : undefined }}
                    className={`card absolute right-0 top-10 z-30 ${width} overflow-hidden py-1 shadow-lg`}
                >
                    {children ? (
                        <div onClick={() => setOpen(false)}>{children}</div>
                    ) : (
                        items.map((action) => (
                            <button
                                key={action.label}
                                onClick={() => {
                                    if (action.disabled || action.busy) return;
                                    setOpen(false);
                                    action.onSelect?.();
                                }}
                                disabled={action.disabled || action.busy}
                                className={`flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                                    action.danger ? "text-red-700 dark:text-red-300" : "text-gray-700 dark:text-gray-200"
                                }`}
                            >
                                {action.busy ? <Spinner size={14} className="mt-0.5 shrink-0" /> : action.icon ? <action.icon size={14} className="mt-0.5 shrink-0" /> : null}
                                <span className="min-w-0">
                                    <span className="block">{action.label}</span>
                                    {action.hint ? <span className="muted block text-xs">{action.hint}</span> : null}
                                </span>
                            </button>
                        ))
                    )}
                </div>
            ) : null}
        </div>
    );
}

// --- toasts -------------------------------------------------------------------

const ToastContext = createContext(() => {});
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const dismiss = useCallback((id) => setToasts((previous) => previous.filter((t) => t.id !== id)), []);

    // `actions` are `{ label, icon, run }`: a toast offering a next step stays
    // long enough to be read and acted on, and goes the moment one is taken.
    const push = useCallback((message, tone = "info", { actions = [] } = {}) => {
        const id = Math.random().toString(36).slice(2);
        setToasts((previous) => [...previous, { id, message, tone, actions }]);
        setTimeout(() => dismiss(id), actions.length ? 20_000 : 6000);
    }, [dismiss]);

    return (
        <ToastContext.Provider value={push}>
            {children}
            <div className="pointer-events-none fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-50 flex flex-col items-end gap-2 sm:inset-x-auto sm:right-4">
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        className={`card pointer-events-auto flex w-full max-w-sm items-start gap-2 px-3 py-2 text-sm shadow-lg ${
                            toast.tone === "error" ? "border-red-300 dark:border-red-900" : ""
                        }`}
                    >
                        {toast.tone === "error" ? (
                            <AlertTriangle size={15} className="mt-0.5 shrink-0 text-red-500" />
                        ) : (
                            <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-green-500" />
                        )}
                        <div className="min-w-0 flex-1">
                            <span className="text-gray-700 dark:text-gray-200">{toast.message}</span>
                            {toast.actions.length ? (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {toast.actions.map(({ label, icon: Icon, run }) => (
                                        <button
                                            key={label}
                                            className="btn-ghost"
                                            onClick={() => {
                                                dismiss(toast.id);
                                                run();
                                            }}
                                        >
                                            {Icon ? <Icon size={14} /> : null} {label}
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                        {toast.actions.length ? (
                            <button className="muted shrink-0" aria-label="Dismiss" onClick={() => dismiss(toast.id)}>
                                <X size={15} />
                            </button>
                        ) : null}
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

// --- sessions -----------------------------------------------------------------

/**
 * How to say that a working copy's last session is over.
 *
 * A background session does not run for ever: Claude Code retires one that has
 * been idle for eight hours, and the Claude app then reports the remote control
 * as disconnected. Nothing is lost — the conversation is on disk and resumes —
 * but a row that just stopped saying "running" one morning read as a failure and
 * sent a real afternoon into the daemon's logs. So it is said out loud, and the
 * reason travels in the title for whoever wants it.
 *
 * Continuing does bring the whole conversation back, and it *does* appear in the
 * Claude app as a new session. That is not something to fix here: the old app
 * session can only be reattached for about four hours, and retirement comes at
 * eight, so after a retirement it is always four hours too late. The title says
 * so rather than letting the button promise more than it can do.
 */
export function sessionEnded(last) {
    const retired = last?.state === "done";
    return {
        // No time on it: the listing gives when the session *started*, and
        // "retired 9h ago" for one that started nine hours ago and ended a minute
        // ago would be a plausible-looking lie.
        label: `none · last one ${retired ? "retired" : "stopped"}`,
        why: retired
            ? "Claude retires a background session after eight hours idle. Continuing brings the whole conversation back, under a new session in the Claude app — the old one can only be reattached for about four hours, and retirement comes at eight."
            : "This session was stopped. Continuing brings the whole conversation back, under a new session in the Claude app."
    };
}

// --- time ---------------------------------------------------------------------

/** "3m ago" beats a timestamp when you are watching something happen. */
export function ago(value) {
    if (!value) return "—";
    const seconds = Math.round((Date.now() - new Date(value)) / 1000);
    if (seconds < 45) return "just now";
    const units = [["m", 60], ["h", 3600], ["d", 86400]];
    for (let i = units.length - 1; i >= 0; i -= 1) {
        const [suffix, size] = units[i];
        if (seconds >= size) return `${Math.round(seconds / size)}${suffix} ago`;
    }
    return `${seconds}s ago`;
}

export function duration(ms) {
    if (!ms) return "—";
    if (ms < 1000) return `${ms}ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

/**
 * "42s", "1m 05s", "1h 02m 05s" — for something still going on.
 *
 * Whole seconds, and padded once there is a larger unit in front, so a counter
 * ticking in a pill does not change width on the way from 1m 9s to 1m 10s.
 */
export function elapsed(from, now = Date.now()) {
    if (!from) return "";
    const total = Math.max(0, Math.floor((now - new Date(from).getTime()) / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
    if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
    return `${s}s`;
}

/** The counter itself: ticks once a second for as long as it is on screen. */
export function Elapsed({ since, className = "" }) {
    const now = useNow(1000);
    if (!since) return null;
    return <span className={`whitespace-nowrap tabular-nums ${className}`} data-elapsed>{elapsed(since, now)}</span>;
}
